/**
 * 专享算力服务模块：配置读取、服务器开关机
 */

import { apiService } from './api'
import type {
  ExclusiveComputeSettings,
  ExclusiveComputeSettingsUpdate,
  ExclusiveComputeServerList,
  ExclusiveComputeActionResponse,
  ExclusiveStartMode,
  ExclusiveComputeBatchActionResponse,
} from '@/types/exclusiveCompute'

/**
 * API 响应结构
 */
interface ApiResponseWrapper<T> {
  code: number
  message: string
  data: T
}

const EXCLUSIVE_ENDPOINT = '/api/user/exclusive' as const

/**
 * 获取专享算力配置
 * @returns 专享算力配置
 */
export async function getExclusiveSettings(): Promise<ExclusiveComputeSettings> {
  const response = await apiService.get<ApiResponseWrapper<ExclusiveComputeSettings>>(
    `${EXCLUSIVE_ENDPOINT}/settings`
  )
  return response.data
}

/**
 * 更新专享算力配置
 * @param payload 更新请求
 * @returns 更新后的配置
 */
export async function updateExclusiveSettings(
  payload: ExclusiveComputeSettingsUpdate
): Promise<ExclusiveComputeSettings> {
  const response = await apiService.put<ApiResponseWrapper<ExclusiveComputeSettings>>(
    `${EXCLUSIVE_ENDPOINT}/settings`,
    payload
  )
  return response.data
}

/**
 * 获取专享服务器列表
 * @returns 专享服务器列表
 */
export async function getExclusiveServers(): Promise<ExclusiveComputeServerList> {
  const response = await apiService.get<ApiResponseWrapper<ExclusiveComputeServerList>>(
    `${EXCLUSIVE_ENDPOINT}/servers`
  )
  return response.data
}

/**
 * 批量启动专享服务器
 * @param count 启动数量
 * @param mode 启动语义（target=目标台数，incremental=新增台数）
 * @returns 启动响应
 */
export async function startExclusiveServers(
  count: number,
  mode: ExclusiveStartMode
): Promise<ExclusiveComputeBatchActionResponse> {
  const response = await apiService.post<ApiResponseWrapper<ExclusiveComputeBatchActionResponse>>(
    `${EXCLUSIVE_ENDPOINT}/servers/start`,
    { count, mode }
  )
  return response.data
}

/**
 * 启动专享服务器
 * @param serverId 服务器 ID
 * @returns 启动响应
 */
export async function startExclusiveServer(
  serverId: string
): Promise<ExclusiveComputeActionResponse> {
  const response = await apiService.post<ApiResponseWrapper<ExclusiveComputeActionResponse>>(
    `${EXCLUSIVE_ENDPOINT}/servers/${serverId}/start`
  )
  return response.data
}

/**
 * 关闭专享服务器
 * @param serverId 服务器 ID
 * @returns 关闭响应
 */
export async function stopExclusiveServer(
  serverId: string
): Promise<ExclusiveComputeActionResponse> {
  const response = await apiService.post<ApiResponseWrapper<ExclusiveComputeActionResponse>>(
    `${EXCLUSIVE_ENDPOINT}/servers/${serverId}/stop`
  )
  return response.data
}
